import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DateRangeId,
  RecordStatus,
  RecordType,
  RecordTypeFilter,
  WalletConfigDto,
  WalletRecordDto,
  WalletRecordsDto,
  WithdrawPayload,
} from '@/api/walletTypes';
import { apiGet, apiPost, HttpError } from './_base';
import { queryClient } from './queryClient';
import { gameTitle } from './game';
import { useBalanceQuery } from './user';
import { toIsoLocal, toIsoLocalInstant, ianaTzIfNeeded } from '@/lib/datetime';
import { useLocale } from '@/i18n';

// === Real backend types (subset of swagger) ============================

type FeeResponse = { current_price?: number; previous_price?: number };

type RealWithdraw = {
  id?: string;
  amount?: number;
  fee?: number;
  from_address?: string;
  from_chain?: string;
  to_address?: string;
  to_chain?: string;
  token?: string;
  status?: string;
  approval?: string;
  /** Carries the human-readable reason on failed withdraws (set from
   *  AIO callback `Remarks` or admin reject reason). */
  description?: string;
  transactionHash?: string;
  created_at?: number;
  updated_at?: number;
};

const REWARD_TYPE_LABELS: Record<string, Record<string, string>> = {
  vip_upgrade_rewards: {
    en: 'VIP Upgrade Rewards',
    zh: 'VIP升级奖励',
    'zh-tw': 'VIP升級獎勵',
    ja: 'VIPアップグレード報酬',
    ko: 'VIP 업그레이드 보상',
    vi: 'Phần thưởng nâng cấp VIP',
  },
};

type RealUserTransaction = {
  activity_name?: string;
  reward_type?: string;
  amount?: string;
  balance?: string;
  game_name?: string;
  /** Parallel zh-only name backend populates on game-bearing rows via the
   *  ClickHouse join (post `feat/name-zh-clickhouse-join`). Empty on
   *  non-game transactions (deposit/withdraw/etc). */
  game_name_zh?: string;
  game_type?: string;
  transaction_id?: string;
  round_id?: string;
  status?: string;
  timestamp?: number;
  timestamps?: number;
  transaction_type?: string;
  currency?: string;
  chain?: string;
  withdraw_address?: string;
  /** Game provider a provider-transfer row moved funds to or from. */
  provider?: string;
};
type GetUserTransactionsReportRes = {
  transactions?: RealUserTransaction[];
  pagination_info?: {
    page_number?: number;
    page_size?: number;
    total_pages?: number;
    total_items?: number;
  };
};

// === useWalletQuery — composes balance + fee from real APIs =============
//
// Currency / network options are NOT fetched: they're UI configuration
// living in `src/pages/Wallet/constants.ts`. The settings/withdraw-support
// endpoint adds a fetch dependency that breaks the picker whenever it
// returns []. Only fields that are actually data — balance, fee — flow
// through this composite.

export function useWithdrawFeeQuery() {
  return useQuery({
    queryKey: ['withdraw-fee'],
    queryFn: () => apiGet<FeeResponse>('/api/v1/settings/withdraw/fee'),
  });
}

function trunc2(s: string): string {
  const dot = s.indexOf('.');
  return dot === -1 || s.length <= dot + 3 ? s : s.slice(0, dot + 3);
}

function fmt2(n: number): string {
  return trunc2(String(n));
}

export function useWalletQuery() {
  const fee = useWithdrawFeeQuery();
  const balance = useBalanceQuery();

  // Each field falls back independently. Balance shows as soon as
  // /user/balance returns — a slow or failing /settings/withdraw/fee
  // call no longer keeps the user staring at "Available balance: 0".
  const balanceNumber = balance.data?.cash_balance ?? 0;
  const feeNumber = fee.data?.current_price ?? 0;
  const data: WalletConfigDto = {
    balance: fmt2(balanceNumber),
    fee: fmt2(feeNumber),
  };

  // Surface raw numbers alongside formatted strings: Withdraw.tsx needs
  // them for the canPreview / fillMax / actualReceived math, which would
  // otherwise stringify-then-Number() the formatted values and choke on
  // the thousands separators (`Number("5,000.00")` → NaN, leaving the
  // Preview button disabled even when the form is valid).
  return {
    data,
    balanceNumber,
    feeNumber,
    isLoading: balance.isLoading,
    error: fee.error ?? balance.error,
  };
}

// === F2: Withdraw mutation → POST /api/v1/transactions/withdraw =========
//
// Body field map: currency→token, network→to_chain (and from_chain), address→
// to_address, otp→code. `from_address` and `fee` must be supplied by the
// caller (Withdraw.tsx pulls them from /user/wallets + useWithdrawFeeQuery
// respectively). Payment-password verification is a SEPARATE call —
// `POST /api/v1/user/payment-password/verify` — done before this mutation;
// the password is never part of the withdraw payload.

export type WithdrawSubmitPayload = WithdrawPayload & {
  fromAddress: string;
  feeAmount: number;
  // Client-generated UUID, stable across retries of the SAME submit, so a
  // double-submit debits + pays out at most once (deduped server-side).
  idempotencyKey: string;
};

const CHAIN_FULL_NAMES: Record<string, string> = {
  ETH: 'Ethereum',
  TRX: 'Tron',
};
function toChainName(id: string): string {
  return CHAIN_FULL_NAMES[id] ?? id;
}

export function useWithdraw() {
  return useMutation({
    mutationFn: (p: WithdrawSubmitPayload) =>
      apiPost<RealWithdraw>('/api/v1/transactions/withdraw', {
        token: p.currency,
        to_chain: toChainName(p.network),
        from_chain: toChainName(p.network),
        to_address: p.address,
        from_address: p.fromAddress,
        amount: Number(p.amount),
        fee: p.feeAmount,
        description: '',
        code: p.otp ?? '',
        idempotency_key: p.idempotencyKey,
      }),
  });
}

// === F3: Wallet records list — multi-source composer ====================
// The Transactions screen is backed by the unified
// /api/v1/analytics/user/transactions response. It now returns a running
// balance per row, and non-game rows should display their raw
// `transaction_type` as the title. Detail pages still use the richer
// deposit/withdraw by-id endpoints when navigated from those legacy flows.

// Authoritative list of backend `transaction_type` values lives in
// services/analytics/internal/pkg/clickhouse/transaction.go (TransactionType*
// constants). Every value the backend emits must be represented here — the
// `?? 'Activities'` fallback in `txToRecord` will silently bucket unknown
// types as "Activity reward" otherwise. The previous fix missed:
//   - `withdraw-rollback` (refunded withdraws — belongs in Withdraw bucket)
//   - `collect-recharge` (operator-collected on-chain top-ups — Deposit)
// All other constants (`deposit`, `withdraw`, `game-bet`, `game-win`,
// `game-refund`, `game-rollback`, `instant-recharge`, `reward`, `rebate`,
// `commission`, `agent-fixed-commission`) are covered. Aliases (`activity`, `activities`,
// `agent-commission`, `withdrawal`) are kept for safety even though they
// are not produced by the canonical backend constants today.
const TX_TYPE_TO_RECORD: Record<string, RecordType> = {
  rebate: 'Rebate',
  reward: 'Activities',
  activity: 'Activities',
  activities: 'Activities',
  'game-bet': 'Games',
  'game-win': 'Games',
  'game-refund': 'Games',
  'game-rollback': 'Games',
  commission: 'Commission',
  'agent-commission': 'Commission',
  'agent-fixed-commission': 'Commission',
  deposit: 'Deposit',
  'instant-recharge': 'Deposit',
  'collect-recharge': 'Deposit',
  withdraw: 'Withdraw',
  withdrawal: 'Withdraw',
  'withdraw-rollback': 'Withdraw',
};

// FE bucket → backend `transaction_type` values to fan out across the
// analytics endpoint. `null` = bucket comes from a different endpoint
// (Deposit/Withdraw) and shouldn't hit analytics at all. `[]` = no filter
// (the 'All' bucket — backend treats empty as "any type").
function analyticsTypesForBucket(
  bucket: RecordTypeFilter | undefined,
): string[] {
  if (!bucket) return [];
  switch (bucket) {
    // The two sweep filters ARE backend `transaction_type` values, so they
    // need no translation — that is why they are spelled that way.
    case 'provider-transfer-in':
    case 'provider-transfer-out':
      return [bucket];
    case 'Deposit':
      // On-chain deposits are stamped "deposit"; operator top-ups are
      // stamped "instant-recharge" or "collect-recharge".
      return ['deposit', 'instant-recharge', 'collect-recharge'];
    case 'Withdraw':
      return ['withdraw', 'withdrawal', 'withdraw-rollback'];
    case 'Rebate':
      return ['rebate'];
    case 'Activities':
      return ['reward', 'activity', 'activities'];
    case 'Commission':
      return ['commission', 'agent-commission', 'agent-fixed-commission'];
    case 'Games':
      return ['game-refund', 'game-bet', 'game-win', 'game-rollback'];
    default:
      return [];
  }
}

function mapStatus(s: string | undefined): RecordStatus {
  const v = (s ?? '').toUpperCase();
  if (!v) return 'Unknown';
  if (v === 'COMPLETED' || v === 'SUCCEED' || v === 'SUCCESS' || v === 'APPROVED') return 'Success';
  if (v === 'PENDING' || v === 'NEW' || v === 'PROCESSING') return 'Pending';
  return 'Failed';
}

function fmtSignedStr(s: string | undefined | null): string {
  const raw = (s ?? '0').replace(/,/g, '').trim();
  const n = parseFloat(raw) || 0;
  const abs = trunc2(raw.startsWith('-') ? raw.slice(1) : raw);
  return n > 0 ? `+${abs}` : n < 0 ? `-${abs}` : abs;
}

function fmtTime(unix: number): string {
  const ms = Math.abs(unix) > 9_999_999_999 ? unix : unix * 1000;
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Translate the records page's date-range picker into RFC3339-with-offset
 * bounds for /api/v1/analytics/user/transactions. The backend parses
 * these via the shared resolveAnalyticsWindow helper; no `tz` param is
 * needed because transactions returns row-level data (no per-day
 * bucketing happens on the server).
 *
 *   'today'      → local midnight today → now (rolling end)
 *   'week'/'month' → now - N days → now (rolling N-day window)
 *   'all' / undefined → no bounds (server returns the default window)
 */
function dateRangeBoundsIso(range: DateRangeId | undefined): { from?: string; to?: string } {
  if (!range || range === 'all') return {};
  const now = new Date();
  if (range === 'today') {
    return { from: toIsoLocal(now, 'start'), to: toIsoLocalInstant(now) };
  }
  const days = range === 'week' ? 7 : 30;
  const start = new Date(now.getTime() - days * 86_400_000);
  return { from: toIsoLocalInstant(start), to: toIsoLocalInstant(now) };
}

// Only these three subtypes have a meaningful game_name to show in the title.
// game-rollback is excluded — it shows the localized type label instead.
const GAME_TITLE_TX_TYPES = new Set(['game-refund', 'game-bet', 'game-win']);

function fmtBalance(value: string | undefined): string {
  if (value == null || value === '') return '--';
  return trunc2(value.replace(/,/g, '').trim()) || '--';
}

function txToRecord(
  t: RealUserTransaction,
  index = 0,
  locale: import('@/i18n/locales/_index').Locale = 'en',
): WalletRecordDto {
  const tt = (t.transaction_type ?? '').toLowerCase();
  const recType = TX_TYPE_TO_RECORD[tt] ?? 'Activities';
  const created = t.timestamp ?? t.timestamps ?? 0;
  const amountNum = parseFloat((t.amount ?? '0').replace(/,/g, '')) || 0;
  // For game rows, prefer the localized game name (zh-only via game_name_zh,
  // others fall through to game_name). Non-game rows leave `title` empty so
  // RecordRow can render the localized bucket label instead of the raw
  // backend `transaction_type` string.
  const isGameRow = GAME_TITLE_TX_TYPES.has(tt);
  const rewardLabel = t.reward_type
    ? (REWARD_TYPE_LABELS[t.reward_type]?.[locale] ?? REWARD_TYPE_LABELS[t.reward_type]?.en ?? '')
    : '';
  const title = isGameRow ? gameTitle(t.game_name, t.game_name_zh, locale) : (t.activity_name || rewardLabel || '');
  return {
    id: `${tt || recType}-${created}-${t.amount ?? '0'}-${index}`,
    type: recType,
    title,
    status: mapStatus(t.status),
    amount: fmtSignedStr(t.amount),
    positive: amountNum >= 0,
    balance: fmtBalance(t.balance),
    timestamp: created ? fmtTime(created) : '',
    currency: t.currency || '',
    network: t.chain || '',
    address: t.withdraw_address || '',
    txHash: t.game_name ?? '',
    rawTransactionType: tt || undefined,
    roundId: t.round_id || undefined,
    transactionId: t.transaction_id || undefined,
    gameType: t.game_type || undefined,
    provider: t.provider || undefined,
  };
}

export function useWalletRecordsQuery(filters?: {
  type?: RecordTypeFilter;
  status?: RecordStatus;
  dateRange?: DateRangeId;
  page?: number;
}) {
  const analyticsTypes = analyticsTypesForBucket(filters?.type);
  const dateRange = filters?.dateRange;
  const status = filters?.status;
  const page = filters?.page ?? 1;
  const locale = useLocale();
  // `tz` is forwarded so half-hour-offset zones (India +05:30, Iran +03:30,
  // Nepal +05:45, Newfoundland -03:30) don't trip the backend's
  // resolveAnalyticsWindow validation, which rejects non-hour RFC3339
  // offsets when tzIANA is empty.
  const tz = ianaTzIfNeeded();

  const analyticsQueryDefs = (analyticsTypes.length === 0 ? [''] : analyticsTypes).map((txType) => ({
        queryKey: [
          'analytics-user-transactions',
          dateRange ?? 'all',
          status ?? 'all',
          txType,
          tz ?? '',
          page,
        ] as const,
        staleTime: 0,
        refetchOnMount: 'always' as const,
        queryFn: () => {
          const { from, to } = dateRangeBoundsIso(dateRange);
          const qs = new URLSearchParams();
          // This endpoint reads start_time/end_time (analytics
          // getusertransactionsreporthandler.go), not from_date/to_date —
          // the latter was silently dropped.
          if (from) qs.set('start_time', from);
          if (to) qs.set('end_time', to);
          qs.set('page_number', String(page));
          qs.set('page_size', '20');
          if (status) qs.set('status', String(status).toLowerCase());
          if (txType) qs.set('transaction_type', txType);
          if (tz) qs.set('tz', tz);
          return apiGet<GetUserTransactionsReportRes>(
            `/api/v1/analytics/user/transactions${qs.toString() ? `?${qs}` : ''}`,
          );
        },
      }));
  const analyticsResults = useQueries({ queries: analyticsQueryDefs });

  const analyticsLoading = analyticsResults.some((q) => q.isLoading);
  const analyticsError = analyticsResults.find((q) => q.error)?.error;

  const isLoading = analyticsLoading;
  const error = analyticsError;

  // Memoize the DTO so the reference is stable across renders — Record.tsx
  // wires `data` into a useEffect dep for infinite-scroll pagination, and a
  // fresh object every render would loop it indefinitely (mirrors the
  // useGameHistoryQuery fix in src/api/mine.ts).
  const filterStatus = filters?.status;
  const dataKey = analyticsResults.map((q) => q.dataUpdatedAt).join('|');
  const data = useMemo<(WalletRecordsDto & { hasMore: boolean }) | undefined>(() => {
    if (isLoading) return undefined;
    const items: WalletRecordDto[] = [];
    for (const q of analyticsResults) {
      const rows = q.data?.transactions ?? [];
      for (const [idx, r] of rows.entries()) {
        items.push(txToRecord(r, idx, locale));
      }
    }

    // Client-side status filter (analytics endpoint accepts a single
    // status string and we already pass it through, but /user/withdraws
    // and /user/deposits ignore the FE bucket if multiple endpoints
    // contribute; keep this as a safety net).
    const filtered = filterStatus
      ? items.filter((i) => i.status === filterStatus)
      : items;
    filtered.sort((a, b) => (a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0));

    // hasMore: check any parallel query's pagination_info; fall back to
    // item-count heuristic when the endpoint doesn't return pagination_info.
    const anyInfo = analyticsResults.find((q) => q.data?.pagination_info)?.data?.pagination_info;
    const hasMore = anyInfo
      ? (anyInfo.page_number ?? 1) < (anyInfo.total_pages ?? 1)
      : items.length >= 20;

    return { items: filtered, hasMore };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, dataKey, filterStatus, locale]);

  return { data, isLoading, error };
}

// === F4: single-record fetch — branch by record type ====================
//
// Navigation from Record.tsx passes the record's `type` via location state,
// so the detail page can fetch from the right endpoint:
//   - Withdraw → /api/v1/transactions/withdraw/{id}
//   - Deposit  → /api/v1/transactions/deposit/{id}
//   - other    → no by-id endpoint exists; read from the list cache
// Backend G1 + G4 land Status/TransactionHash/FailureReason on Withdraw.

export function useWalletRecordQuery(id: string, type: RecordType | undefined) {
  const queryClient = useQueryClient();
  const locale = useLocale();
  return useQuery({
    queryKey: ['wallet-record', type, id],
    enabled: Boolean(id) && Boolean(type),
    queryFn: async () => {
      // All record types (Deposit, Withdraw, Games, etc.) come from the analytics
      // endpoint and carry a synthetic composite id. Re-derive from the cached
      // analytics response (the user just navigated from the list, so it's hot).
      const cached = queryClient
        .getQueriesData<GetUserTransactionsReportRes>({
          queryKey: ['analytics-user-transactions'],
        })
        .flatMap(([, v]) =>
          (v?.transactions ?? [])
            .map((r, i) => txToRecord(r, i, locale))
            .filter((r): r is WalletRecordDto => r !== null),
        );
      const found = cached.find((r) => r.id === id);
      if (!found) throw new HttpError(404, 'Record not found');
      return found;
    },
  });
}

// ── Deposit: supported chains + wallet generation ──────────────────────────

type SupportedChainCurrency = {
  id: string;
  symbol: string;
  address?: string;
};

export type SupportedChainItem = {
  chain: { id: string; name: string };
  currencies: SupportedChainCurrency[];
};

type SupportedChainsResponse = {
  chains: SupportedChainItem[];
};

export type GeneratedWalletEntry = {
  id?: string;
  symbol: string;
  address: string;
  contract_address?: string;
  network?: string;
  amount?: string | number;
};

type GeneratedChainWallet = {
  chain_name: string;
  wallets: GeneratedWalletEntry[];
};

// The envelope unwrapper in _base.ts returns env.data, so the type here
// models data directly: { chain_wallets: [...] } (snake_case, not PascalCase).
export type GenerateWalletResponse = {
  chain_wallets: GeneratedChainWallet[];
};

/** GET /api/v1/settings/withdraw-support — returns chains + currencies
 *  allowed for deposits. Result is cached; the filter logic lives in the
 *  Deposit page since it is presentation-level policy. */
export const useSupportedChainsQuery = () =>
  useQuery({
    queryKey: ['settings', 'supported-chains'],
    queryFn: () => apiGet<SupportedChainsResponse>('/api/v1/settings/withdraw-support'),
  });

/** GET-first / POST-fallback resolver for the Deposit page address.
 *
 *  GET  /api/v1/user/wallets — returns ALL wallets the user already has
 *  POST /api/v1/user/wallets — provisions a wallet for the requested chain
 *
 *  POST hits the external chain provider (AIO LongTermPayIn) on every call;
 *  doing it on every Deposit mount is what surfaced the 500s. We GET first
 *  and only POST when the user has no wallet for the selected chain yet.
 *  Both endpoints return the same { chain_wallets } envelope, so consumers
 *  see one shape regardless of which path ran.
 */
export function useWithdrawalLimitQuery() {
  return useQuery({
    queryKey: ['withdrawal-limit'],
    // The withdrawable cap gates the withdraw form; refetch on focus so a cap
    // that changed server-side (e.g. turnover cleared by a bet) refreshes when
    // the user returns, instead of submitting against a mount-time snapshot.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: () =>
      apiGet<{ limit?: number; withdrawable?: number }>(
        '/api/v1/transactions/user/withdrawalLimit',
      ),
  });
}

export const useDepositAddressQuery = (chainId?: string, chainName?: string) =>
  useQuery({
    queryKey: ['deposit-address', chainId ?? ''],
    enabled: Boolean(chainId && chainName),
    queryFn: async () => {
      const existing = await apiGet<GenerateWalletResponse>('/api/v1/user/wallets');
      const target = chainName!.toLowerCase();
      const hasChain = (existing.chain_wallets ?? []).some(
        (cw) => cw.chain_name?.toLowerCase() === target,
      );
      if (hasChain) return existing;
      return apiPost<GenerateWalletResponse>('/api/v1/user/wallets', {
        chains: [chainId!],
      });
    },
  });

// One sweep at a time. The exit button and the navigation that follows it are
// the same event from the player's point of view, and both trigger a sweep —
// sharing the in-flight promise turns that into a single request instead of a
// race. A later, genuinely separate arrival starts a fresh one.
let sweepInFlight: Promise<void> | null = null;

/**
 * Pulls any balance sitting in a game provider's wallet back into the main
 * wallet. Best-effort and idempotent — safe to call when there is nothing to
 * return, and safe to call from several triggers at once.
 *
 * Refreshes the cached balance on success, since every caller is on their way
 * to a screen that shows it.
 */
export function returnProviderFunds(): Promise<void> {
  if (sweepInFlight) return sweepInFlight;
  sweepInFlight = apiPost<void>('/api/v1/user/provider-wallet/return-funds', {})
    .then(() => {
      queryClient.invalidateQueries({ queryKey: ['user', 'balance'] });
    })
    .finally(() => {
      sweepInFlight = null;
    });
  return sweepInFlight;
}
